import { useState, useEffect } from 'react';
import LoadingFacts from './LoadingFacts';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl, env } from '../config/env.js';
import html2pdf from 'html2pdf.js';

function escapeHtml(str) {
  if (!str) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

const LANGUAGES = [
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  { code: 'ar', name: 'العربية', flag: '🇸🇦' }
];

const ReportPreview = ({ reportData }) => {
  if (!reportData) return null;

  const headerStyle = {
    background: 'linear-gradient(to right, #059669, #10b981)',
    color: 'white',
    padding: '24px'
  };

  const sectionIconStyle = {
    width: '24px',
    height: '24px',
    backgroundColor: '#059669',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  };

  const sectionContentStyle = {
    marginLeft: '32px',
    padding: '12px',
    backgroundColor: '#f9fafb',
    borderRadius: '6px',
    borderLeft: '4px solid #059669'
  };

  return (
    <div style={{
      width: '100%',
      backgroundColor: 'white',
      overflow: 'visible',
      position: 'relative',
      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
      transform: 'scale(0.8)',
      transformOrigin: 'center center',
      minHeight: 'fit-content',
      marginBottom: '2rem',
      marginLeft: 'auto',
      marginRight: 'auto'
    }}>
      {/* Blue Header */}
      <header style={headerStyle}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              height: '40px',
              width: '40px',
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <svg style={{ width: '24px', height: '24px', color: 'white' }} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2L21.5 7v10L12 22 2.5 17V7L12 2z" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                <path d="M9 9h6M12 6v12M9 15h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                <path d="M18.5 5.5l2 2v1l-2-2z" fill="currentColor"/>
              </svg>
            </div>
            <div>
              <h1 style={{ fontSize: '20px', fontWeight: '600', margin: 0, fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}>MediScan - Consultation Report</h1>
              <p style={{ fontSize: '14px', color: '#dbeafe', margin: 0 }}>{reportData.meta.clinic} • {reportData.meta.date}</p>
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <p style={{ fontSize: '12px', color: '#bfdbfe', margin: 0 }}>Record ref.</p>
            <p style={{ fontSize: '18px', fontWeight: 'bold', margin: 0 }}>{reportData.meta.ref}</p>
          </div>
        </div>
      </header>

      {/* Main Content Grid */}
      <div style={{ display: 'flex' }}>
        {/* Left Content */}
        <main style={{ flex: 1, padding: '24px' }}>
          {/* Patient Card */}
          <div style={{ 
            marginBottom: '24px', 
            padding: '16px', 
            backgroundColor: '#f9fafb', 
            borderRadius: '8px', 
            border: '1px solid #e5e7eb' 
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
              <div>
                <h2 style={{ fontSize: '18px', fontWeight: '500', color: '#111827', margin: 0 }}>{reportData.patient.name}</h2>
                <p style={{ fontSize: '14px', color: '#6b7280', margin: '4px 0' }}>ID {reportData.patient.id}</p>
                <div style={{ marginTop: '8px', display: 'flex', gap: '8px' }}>
                  <span style={{ 
                    padding: '4px 8px', 
                    fontSize: '12px', 
                    backgroundColor: '#e5e7eb', 
                    borderRadius: '4px' 
                  }}>{reportData.patient.age} ans</span>
                  <span style={{ 
                    padding: '4px 8px', 
                    fontSize: '12px', 
                    backgroundColor: '#e5e7eb', 
                    borderRadius: '4px' 
                  }}>{reportData.patient.sex}</span>
                </div>
              </div>
              <div style={{ textAlign: 'right', fontSize: '14px', color: '#6b7280' }}>
                <p style={{ margin: 0 }}>{reportData.meta.practitioner}</p>
              </div>
            </div>
          </div>

          {/* Medical Sections */}
          <div>
            {[
              { key: 'motif_consultation', title: 'Chief Complaint', content: reportData.soap.motif },
              { key: 'histoire_maladie', title: 'History of Present Illness', content: reportData.soap.histoire_maladie },
              { key: 'antecedents', title: 'Past Medical History', content: reportData.soap.antecedents },
              { key: 'examen_clinique', title: 'Clinical Examination', content: reportData.soap.examen_clinique },
              { key: 'examens_complementaires', title: 'Additional Tests', content: reportData.soap.examens_complementaires },
              { key: 'conclusion_traitement', title: 'Conclusion and Treatment Plan', content: reportData.soap.conclusion_traitement },
              { key: 'ordonnance', title: 'Prescription', content: reportData.soap.ordonnance, isList: true }
            ].map(section => (
              <div key={section.key} style={{ marginBottom: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                  <div style={sectionIconStyle}>
                    <div style={{ height: '8px', width: '8px', backgroundColor: 'white', borderRadius: '50%' }}></div>
                  </div>
                  <h3 style={{ 
                    fontSize: '14px', 
                    fontWeight: '600', 
                    color: '#374151', 
                    textTransform: 'uppercase', 
                    letterSpacing: '0.05em',
                    margin: 0
                  }}>{section.title}</h3>
                </div>
                <div style={sectionContentStyle}>
                  {section.isList ? (
                    <div style={{ fontSize: '14px', color: '#374151', lineHeight: '1.6' }}>
                      {section.content.split('. ').map((line, i) => {
                        const trimmedLine = line.trim();
                        return trimmedLine ? (
                          <p key={i} style={{
                            marginBottom: '6px',
                            margin: '4px 0',
                            wordWrap: 'break-word',
                            overflowWrap: 'break-word',
                            hyphens: 'auto'
                          }}>
                            • {trimmedLine}{!trimmedLine.endsWith('.') ? '.' : ''}
                          </p>
                        ) : null;
                      })}
                    </div>
                  ) : (
                    <p style={{
                      fontSize: '14px',
                      color: '#374151',
                      margin: 0,
                      lineHeight: '1.6',
                      wordWrap: 'break-word',
                      overflowWrap: 'break-word',
                      hyphens: 'auto',
                      whiteSpace: 'pre-wrap',
                      fontFamily: "'Roboto', 'Source Sans Pro', system-ui, sans-serif"
                    }}>
                      {section.content}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </main>

        {/* Right Sidebar */}
        <aside style={{ 
          width: '256px', 
          backgroundColor: '#f9fafb', 
          padding: '16px', 
          borderLeft: '1px solid #e5e7eb' 
        }}>
          <div style={{ marginBottom: '24px' }}>
            <h4 style={{ 
              fontSize: '12px', 
              fontWeight: '600', 
              color: '#6b7280', 
              textTransform: 'uppercase', 
              letterSpacing: '0.1em', 
              marginBottom: '8px',
              margin: '0 0 8px 0'
            }}>Consultation info</h4>
            <div style={{ 
              fontSize: '12px', 
              color: '#6b7280', 
              backgroundColor: 'white', 
              padding: '12px', 
              borderRadius: '6px', 
              border: '1px solid #e5e7eb' 
            }}>
              <p style={{ margin: 0 }}>{reportData.soap.consultation || 'Consultation information'}</p>
            </div>
          </div>

          <div style={{ marginBottom: '24px' }}>
            <h4 style={{ 
              fontSize: '12px', 
              fontWeight: '600', 
              color: '#6b7280', 
              textTransform: 'uppercase', 
              letterSpacing: '0.1em', 
              marginBottom: '8px',
              margin: '0 0 8px 0'
            }}>Vital Signs</h4>
            <div style={{
              fontSize: '11px',
              color: '#4b5563',
              backgroundColor: 'white',
              padding: '10px 12px',
              borderRadius: '6px',
              border: '1px solid #e5e7eb',
              lineHeight: '1.8',
              whiteSpace: 'pre-line'
            }}>
              {reportData.soap.constantes && reportData.soap.constantes !== 'Vital signs not recorded' ?
                reportData.soap.constantes :
                <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>Vital signs not recorded</span>
              }
            </div>
          </div>
          
          <div style={{ marginBottom: '24px' }}>
            <h4 style={{ 
              fontSize: '12px', 
              fontWeight: '600', 
              color: '#6b7280', 
              textTransform: 'uppercase', 
              letterSpacing: '0.1em', 
              marginBottom: '8px',
              margin: '0 0 8px 0'
            }}>Details</h4>
            <div>
              <p style={{ fontSize: '14px', color: '#374151', margin: '8px 0' }}>Auto-generated</p>
              <p style={{ fontSize: '14px', color: '#374151', margin: '8px 0' }}>Verification required</p>
            </div>
          </div>
        </aside>
      </div>

      {/* Footer */}
      <footer style={{ 
        backgroundColor: '#f3f4f6', 
        padding: '16px', 
        borderTop: '1px solid #e5e7eb', 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center' 
      }}>
        <div style={{ textAlign: 'left' }}>
          <p style={{ fontSize: '12px', fontWeight: '600', color: '#1f2937', margin: '0 0 8px 0' }}>PHYSICIAN SIGNATURE</p>
          <div style={{ 
            marginTop: '8px', 
            border: '2px dashed #d1d5db', 
            borderRadius: '4px', 
            padding: '16px', 
            width: '128px', 
            height: '64px', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center' 
          }}>
            <span style={{ fontSize: '12px', color: '#9ca3af' }}>Stamp and signature</span>
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <p style={{ fontSize: '12px', fontWeight: '600', color: '#1f2937', margin: '0 0 10px 0', textTransform: 'uppercase', letterSpacing: '0.5px' }}>DATE AND LOCATION</p>
          <div style={{ marginTop: '10px' }}>
            <p style={{ fontSize: '12px', margin: '6px 0' }}>
              Location: <span style={{ fontSize: '15px' }}>{reportData.meta.clinic || '________________'}</span>
            </p>
            <p style={{ fontSize: '12px', margin: '6px 0' }}>
              Date: <span style={{ fontSize: '15px' }}>{reportData.meta.date || '________________'}</span>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

// Define which sections are critical for validation (all medical sections)
const CRITICAL_SECTIONS = [
  'motif_consultation',
  'histoire_maladie',
  'antecedents',
  'examen_clinique',
  'examens_complementaires',
  'ordonnance',
  'conclusion_traitement'
];

// Check for empty sections
const validateExtractedSections = (sections) => {
  const emptySections = [];

  CRITICAL_SECTIONS.forEach(key => {
    const value = sections[key]?.trim() || '';

    // Check if section is empty or contains only placeholder text
    const isEmpty = value.length < 10;
    const isPlaceholder = /^\[.*\]$/.test(value); // Matches [placeholder text]
    const startsWithBracket = value.startsWith('[') && value.includes(']');

    if (isEmpty || isPlaceholder || startsWithBracket) {
      emptySections.push(key);
    }
  });

  return {
    isValid: emptySections.length < 2,
    emptySections,
    emptyCount: emptySections.length
  };
};

// Get label for section key
const getSectionLabel = (key) => {
  const labels = {
    motif_consultation: 'Chief Complaint',
    histoire_maladie: 'History of Present Illness',
    antecedents: 'Past Medical History',
    examen_clinique: 'Clinical Examination',
    examens_complementaires: 'Additional Tests',
    ordonnance: 'Prescription',
    conclusion_traitement: 'Conclusion'
  };
  return labels[key] || key;
};

const EditReport = ({ reportData, patientInfo, patientConstantes, getRecordedAudio, clearRecordedAudio, isExistingConsultation, onSave, onCancel, onNavigateHome }) => {
  const { user, authFetch } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [isExtractingFacts, setIsExtractingFacts] = useState(true);
  const [doctorInfo, setDoctorInfo] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [audioUploaded, setAudioUploaded] = useState(false);

  // Validation state for extraction quality
  const [validationWarning, setValidationWarning] = useState(null);
  const [highlightedSections, setHighlightedSections] = useState([]);

  // Debug logging
  console.log('EditReport received patientInfo:', patientInfo);
  console.log('EditReport received patientConstantes:', patientConstantes);
  console.log('🎙️ EditReport checking audio on mount...');
  if (getRecordedAudio) {
    const audioBlob = getRecordedAudio();
    if (audioBlob) {
      console.log('✅ EditReport: Audio blob available on mount:', audioBlob.size, 'bytes');
    } else {
      console.warn('⚠️ EditReport: No audio blob available on mount!');
    }
  } else {
    console.warn('❌ EditReport: getRecordedAudio function not provided!');
  }
  
  // Translation state
  const [currentLanguage, setCurrentLanguage] = useState('fr');
  const [originalLanguage] = useState('fr');
  const [translations, setTranslations] = useState({});
  const [isTranslating, setIsTranslating] = useState(false);
  const [targetLanguage, setTargetLanguage] = useState('');
  const [reportId] = useState(() => `RPT-${Date.now()}`);
  
  // Separate fields for each section - new medical structure
  const [sections, setSections] = useState({
    consultation: '',
    patient: '',
    medecin: '',
    motif_consultation: '',
    histoire_maladie: '',
    antecedents: '',
    constantes: '',
    examen_clinique: '',
    examens_complementaires: '',
    ordonnance: '',
    conclusion_traitement: ''
  });

  // Separate fields for vital signs - initialize from patientConstantes prop
  const [constantes, setConstantes] = useState(() => {
    if (patientConstantes && Object.keys(patientConstantes).length > 0) {
      console.log('✅ Initializing constantes from patientConstantes prop:', patientConstantes);
      return patientConstantes;
    }
    return {
      temperature: '',
      tension: '',
      frequenceCardiaque: '',
      saturation: '',
      glycemie: '',
      poids: '',
      taille: ''
    };
  });

  // Update constantes when patientConstantes prop changes
  useEffect(() => {
    if (patientConstantes && Object.keys(patientConstantes).some(key => patientConstantes[key])) {
      console.log('📊 Updating constantes from patientConstantes prop:', patientConstantes);
      setConstantes(patientConstantes);
    }
  }, [patientConstantes]);

  useEffect(() => {
    // Get doctor info from localStorage
    try {
      const info = JSON.parse(localStorage.getItem('doctorInfo')) || {};
      setDoctorInfo(info);
    } catch (e) {
      console.error('Error loading doctor info:', e);
    }
  }, []);

  useEffect(() => {
    const loadSections = async () => {
      // Check if we have pre-extracted facts from the loading screen
      if (reportData && typeof reportData === 'object' && reportData.extractedFacts) {
        console.log('✅ Using pre-extracted medical facts from loading screen');
        
        const facts = reportData.extractedFacts;

        // Auto-generate consultation text if not provided
        const autoConsultation = facts.consultation?.value || (() => {
          const date = new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
          const time = new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
          const location = doctorInfo.place || 'Medical office';
          const type = doctorInfo.consultationType || 'General medicine consultation';
          return `Consultation on ${date} at ${time}, ${location}, ${type}`;
        })();

        const extractedSections = {
          consultation: autoConsultation,
          patient: facts.patient?.value || '',
          medecin: facts.medecin?.value || '',
          motif_consultation: facts.motif_consultation?.value || '',
          histoire_maladie: facts.histoire_maladie?.value || '',
          antecedents: facts.antecedents?.value || '',
          constantes: facts.constantes?.value || '',
          examen_clinique: facts.examen_clinique?.value || '',
          examens_complementaires: facts.examens_complementaires?.value || '',
          ordonnance: facts.ordonnance?.value || '',
          conclusion_traitement: facts.conclusion_traitement?.value || ''
        };
        
        setSections(extractedSections);
        setTranslations(prev => ({
          ...prev,
          [originalLanguage]: extractedSections
        }));

        // Load structured constantes if available
        if (facts.constantes?.structured) {
          setConstantes(facts.constantes.structured);
        }

        // Validate extraction quality - check for empty sections
        const validation = validateExtractedSections(extractedSections);
        if (!validation.isValid) {
          console.log('⚠️ Extraction validation warning:', validation.emptyCount, 'empty sections');
          setValidationWarning({
            emptyCount: validation.emptyCount,
            emptySections: validation.emptySections
          });
          setHighlightedSections(validation.emptySections);
        }

        setIsExtractingFacts(false);

      } else if (reportData && typeof reportData === 'string' && reportData.trim()) {
        // Legacy support - if we get a string, try to extract facts
        setIsExtractingFacts(true);
        
        try {
          console.log('🤖 Legacy extraction: Processing string data...');
          const parsed = await parseReportData(reportData);
          
          setSections(parsed);
          setTranslations(prev => ({
            ...prev,
            [originalLanguage]: parsed
          }));
          
          console.log('✅ Fact extraction completed successfully');
        } catch (error) {
          console.error('❌ Error extracting facts:', error);
          const emptySections = {
            consultation: '',
            patient: '',
            medecin: '',
            motif_consultation: '',
            histoire_maladie: reportData,
            antecedents: '',
            constantes: '',
            examen_clinique: '',
            examens_complementaires: '',
            ordonnance: '',
            conclusion_traitement: ''
          };
          setSections(emptySections);
          setTranslations(prev => ({
            ...prev,
            [originalLanguage]: emptySections
          }));
        } finally {
          setIsExtractingFacts(false);
        }
      } else {
        // Set empty sections for new report
        const emptySections = {
          consultation: '',
          patient: '',
          medecin: '',
          motif_consultation: '',
          histoire_maladie: '',
          antecedents: '',
          constantes: '',
          examen_clinique: '',
          examens_complementaires: '',
          ordonnance: '',
          conclusion_traitement: ''
        };
        setSections(emptySections);
        // Store original empty sections
        setTranslations(prev => ({
          ...prev,
          [originalLanguage]: emptySections
        }));
        setIsExtractingFacts(false);
      }
    };

    loadSections();
  }, [reportData, doctorInfo, originalLanguage]);

  const parseReportData = async (data) => {
    if (!data || !data.trim()) {
      return {
        consultation: '',
        patient: '',
        medecin: '',
        motif_consultation: '',
        histoire_maladie: '',
        antecedents: '',
        constantes: '',
        examen_clinique: '',
        examens_complementaires: '',
        ordonnance: '',
        conclusion_traitement: ''
      };
    }

    try {
      // Use dedicated extraction endpoint for structured medical fact extraction
      const response = await fetch('/api/extract-report-data', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: data
        }),
      });

      if (response.ok) {
        const result = await response.json();
        
        // Check if the response contains extracted facts
        if (result.extracted_facts) {
          const facts = result.extracted_facts;
          return {
            consultation: facts.consultation?.value || '',
            patient: facts.patient?.value || '',
            medecin: facts.medecin?.value || '',
            motif_consultation: facts.motif_consultation?.value || '',
            histoire_maladie: facts.histoire_maladie?.value || '',
            antecedents: facts.antecedents?.value || '',
            constantes: facts.constantes?.value || '',
            examen_clinique: facts.examen_clinique?.value || '',
            examens_complementaires: facts.examens_complementaires?.value || '',
            ordonnance: facts.ordonnance?.value || '',
            conclusion_traitement: facts.conclusion_traitement?.value || ''
          };
        }

        // Fallback to putting summary in histoire_maladie if no extracted facts
        return {
          consultation: '',
          patient: '',
          medecin: '',
          motif_consultation: '',
          histoire_maladie: result.summary || data,
          antecedents: '',
          constantes: '',
          examen_clinique: '',
          examens_complementaires: '',
          ordonnance: '',
          conclusion_traitement: ''
        };
      }
    } catch (error) {
      console.error('AI categorization failed:', error);
    }

    // Final fallback - put everything in histoire_maladie
    return {
      consultation: '',
      patient: '',
      medecin: '',
      motif_consultation: '',
      histoire_maladie: data,
      antecedents: '',
      constantes: '',
      examen_clinique: '',
      examens_complementaires: '',
      ordonnance: '',
      conclusion_traitement: ''
    };
  };

  const handleSave = async () => {
    setIsLoading(true);
    try {
      // Generate final report from sections
      const finalReport = generateFinalReport();
      console.log('Saving report:', finalReport);
      
      // Simulate save operation
      await new Promise(resolve => setTimeout(resolve, 500));
      
      if (onSave) {
        onSave(finalReport);
      }
      
      alert('✅ Report saved successfully!');
    } catch (error) {
      console.error('Error saving report:', error);
      alert('❌ Error while saving the report');
    } finally {
      setIsLoading(false);
    }
  };

  const generateFinalReport = () => {
    const currentDate = new Date().toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
    const currentTime = new Date().toLocaleTimeString('en-US');

    // Generate report data matching the asset/report.jsx structure
    const reportData = {
      meta: {
        clinic: doctorInfo.place || 'Medical Center',
        practitioner: `Dr. ${doctorInfo.name || 'Not specified'} (${doctorInfo.specialty || 'General medicine'})`,
        date: currentDate,
        ref: `CONS-${new Date().toISOString().split('T')[0]}-${currentTime.replace(/:/g, 'h').replace(/h\d+$/, 'h')}`
      },
      patient: {
        id: `P-${new Date().getFullYear()}-${String(Math.floor(Math.random() * 9999)).padStart(4, '0')}`,
        name: patientInfo && (patientInfo.nom || patientInfo.prenom) ?
              `${patientInfo.prenom || ''} ${patientInfo.nom || ''}`.trim() :
              "Patient (anonymized)",
        age: patientInfo && patientInfo.dateNaissance ?
             new Date().getFullYear() - new Date(patientInfo.dateNaissance).getFullYear() :
             "Not specified",
        sex: patientInfo && patientInfo.sexe ?
             (patientInfo.sexe === 'M' ? 'Male' : 'Female') :
             "Not specified",
        weight: constantes && constantes.poids ?
                `${constantes.poids} kg` :
                "Not specified",
        height: constantes && constantes.taille ?
                `${constantes.taille} cm` :
                "Not specified",
        allergies: ["None known"],
        comorbidities: ["None known"]
      },
      soap: {
        motif: sections.motif_consultation || '[Not filled in]',
        histoire_maladie: sections.histoire_maladie || '[Not filled in]',
        antecedents: sections.antecedents || '[Not filled in]',
        examen_clinique: sections.examen_clinique || '[Not filled in]',
        examens_complementaires: sections.examens_complementaires || '[Not filled in]',
        conclusion_traitement: sections.conclusion_traitement || '[Not filled in]',
        ordonnance: sections.ordonnance || '[Not filled in]',
        suivi: '[Not filled in]',
        observations: '[Not filled in]',
        constantes: generateConstantesText()
      }
    };

    // Format as professional medical report following the asset/report.jsx template
    return `
╔══════════════════════════════════════════════════════════════════════════════╗
║                            CONSULTATION REPORT                               ║
║                                                                              ║
║  ${reportData.meta.clinic.padEnd(46)} Ref: ${reportData.meta.ref.padStart(20)} ║
║  ${reportData.meta.practitioner.padEnd(68)} ║
║  ${reportData.meta.date.padEnd(68)} ║
╠══════════════════════════════════════════════════════════════════════════════╣
║                                                                              ║
║  PATIENT: ${reportData.patient.name.padEnd(27)} ID: ${reportData.patient.id.padEnd(17)} ║
║                                                                              ║
╠══════════════════════════════════════════════════════════════════════════════╣
║                                                                              ║
║  🔹 CHIEF COMPLAINT                                                          ║
║                                                                              ║
${reportData.soap.motif.split('\n').map(line => `║  ${line.padEnd(76)} ║`).join('\n')}
║                                                                              ║
║  🔹 HISTORY OF PRESENT ILLNESS                                               ║
║                                                                              ║
${reportData.soap.histoire_maladie.split('\n').map(line => `║  ${line.padEnd(76)} ║`).join('\n')}
║                                                                              ║
║  🔹 PAST MEDICAL HISTORY                                                     ║
║                                                                              ║
${reportData.soap.antecedents.split('\n').map(line => `║  ${line.padEnd(76)} ║`).join('\n')}
║                                                                              ║
║  🔹 VITAL SIGNS                                                              ║
║                                                                              ║
${reportData.soap.constantes.split('\n').map(line => `║  ${line.padEnd(76)} ║`).join('\n')}
║                                                                              ║
║  🔹 CLINICAL EXAMINATION                                                     ║
║                                                                              ║
${reportData.soap.examen_clinique.split('\n').map(line => `║  ${line.padEnd(76)} ║`).join('\n')}
║                                                                              ║
║  🔹 ADDITIONAL TESTS                                                         ║
║                                                                              ║
${reportData.soap.examens_complementaires.split('\n').map(line => `║  ${line.padEnd(76)} ║`).join('\n')}
║                                                                              ║
║  🔹 PRESCRIPTION                                                             ║
║                                                                              ║
${reportData.soap.ordonnance.split('\n').map(line => `║  ${line.padEnd(76)} ║`).join('\n')}
║                                                                              ║
║  🔹 CONCLUSION AND TREATMENT PLAN                                            ║
║                                                                              ║
${reportData.soap.conclusion_traitement.split('\n').map(line => `║  ${line.padEnd(76)} ║`).join('\n')}
║                                                                              ║
╠══════════════════════════════════════════════════════════════════════════════╣
║                                                                              ║
║  📋 AI-assisted – human verification required                              ║
║                                                                              ║
║  Location: ${(doctorInfo.place || 'Medical office').padEnd(55)} ║
║  Date    : ${new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }).padEnd(56)} ║
║                                                                              ║
║  ${reportData.meta.practitioner.padEnd(68)} ║
║  Signature: ____________________________                                     ║
║                                                                              ║
╚══════════════════════════════════════════════════════════════════════════════╝
`;
  };



  const handleExport = async () => {
    // Debug patient info
    console.log('=== EXPORT DEBUG ===');
    console.log('Patient Info FULL:', JSON.stringify(patientInfo, null, 2));
    console.log('Patient Info prenom:', patientInfo?.prenom);
    console.log('Patient Info nom:', patientInfo?.nom);
    console.log('Patient Info dateNaissance:', patientInfo?.dateNaissance);
    console.log('Patient Info sexe:', patientInfo?.sexe);
    console.log('Patient Info adresse:', patientInfo?.adresse);
    console.log('User:', user);
    console.log('User ID:', user?.id);
    console.log('User TenantID:', user?.tenantId);
    console.log('User Name:', user?.name);
    console.log('User Specialty:', user?.specialty);
    console.log('Sections:', sections);
    console.log('Sections motif_consultation:', sections.motif_consultation);
    console.log('Sections histoire_maladie:', sections.histoire_maladie);
    console.log('===================');

    // First, save the consultation to the database (only for new consultations, not when viewing existing ones)
    // Skipped entirely in demo mode — no DynamoDB backing, PDF export still runs
    if (!env.IS_DEMO && !isExistingConsultation && user && patientInfo && patientInfo.prenom && patientInfo.nom && patientInfo.dateNaissance) {
      setIsSaving(true);
      setSaveError(null);

      try {
        // Validate required patient fields
        if (!patientInfo.prenom || !patientInfo.nom || !patientInfo.dateNaissance) {
          throw new Error('Incomplete patient information (last name, first name, date of birth required)');
        }

        // Build patient text for report
        const patientAge = new Date().getFullYear() - new Date(patientInfo.dateNaissance).getFullYear();
        const patientSex = patientInfo.sexe === 'M' ? 'Male' : 'Female';
        const patientFullName = `${patientInfo.prenom} ${patientInfo.nom}`;

        const consultationData = {
          // Doctor info from auth
          doctor_name: user.name,
          doctor_specialty: user.specialty,

          // Patient info
          patient_first_name: patientInfo.prenom,
          patient_last_name: patientInfo.nom,
          patient_dob: patientInfo.dateNaissance, // Should be YYYY-MM-DD
          patient_sex: patientInfo.sexe || '',
          patient_weight: constantes.poids || '',
          patient_height: constantes.taille || '',
          patient_phone: patientInfo.phone || '',
          patient_email: patientInfo.email || '',
          patient_address: patientInfo.adresse || '',

          // Report sections (use current values from sections state)
          consultation: sections.consultation || '',
          patient: `${patientFullName}, ${patientAge} ans, ${patientSex}`,
          medecin: `${user.name}, ${user.specialty}`,
          motif_consultation: sections.motif_consultation || '',
          histoire_maladie: sections.histoire_maladie || '',
          antecedents: sections.antecedents || '',
          constantes: generateConstantesText(),
          examen_clinique: sections.examen_clinique || '',
          examens_complementaires: sections.examens_complementaires || '',
          ordonnance: sections.ordonnance || '',
          conclusion_traitement: sections.conclusion_traitement || '',

          // Metadata
          consultation_type: doctorInfo.consultationType || 'General consultation',
          consultation_place: doctorInfo.place || 'Medical Office',
        };

        const response = await authFetch(getApiUrl('/api/consultations/save'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(consultationData),
        });

        const result = await response.json();

        if (!result.success) {
          throw new Error(result.message || 'Save failed');
        }

        console.log('Consultation saved successfully:', result);

        // Upload audio if available and not already uploaded
        console.log('🎙️  Checking for recorded audio...');
        if (audioUploaded) {
          console.log('✅ Audio already uploaded, skipping...');
        } else if (getRecordedAudio) {
          console.log('✅ getRecordedAudio function is available');
          const audioBlob = getRecordedAudio();
          if (audioBlob && audioBlob.size > 0) {
            console.log('🎵 Uploading audio recording:', audioBlob.size, 'bytes, type:', audioBlob.type);
            try {
              const formData = new FormData();
              formData.append('audio', audioBlob, 'consultation-audio.webm');
              formData.append('consultation_id', result.consultation_id);

              console.log('📤 Sending audio to server...');
              const audioResponse = await authFetch(getApiUrl('/api/consultations/upload-audio'), {
                method: 'POST',
                body: formData,
              });

              console.log('📥 Server response status:', audioResponse.status);
              const audioResult = await audioResponse.json();

              if (audioResult.success) {
                console.log('✅ Audio uploaded successfully:', audioResult.audio_path);
                // Mark audio as uploaded to prevent re-uploading
                setAudioUploaded(true);
                // Clear the recorded audio from memory after successful upload
                if (clearRecordedAudio) {
                  clearRecordedAudio();
                }
              } else {
                console.error('❌ Audio upload failed:', audioResult.message);
                alert('⚠️ Audio upload failed: ' + audioResult.message);
              }
            } catch (audioError) {
              console.error('❌ Failed to upload audio:', audioError);
              alert('⚠️ Failed to upload audio: ' + audioError.message);
            }
          } else {
            console.warn('⚠️  No audio recording available to upload (blob is null or empty)');
            if (!audioBlob) {
              console.warn('   - audioBlob is null/undefined');
            } else {
              console.warn('   - audioBlob size is 0');
            }
          }
        } else {
          console.warn('❌ getRecordedAudio function is not available!');
        }
      } catch (error) {
        console.error('Failed to save consultation:', error);
        setSaveError(error.message);
        // Still allow export even if save fails
      } finally {
        setIsSaving(false);
      }
    } else if (!env.IS_DEMO && !isExistingConsultation) {
      // Patient info is missing or incomplete - show warning (only for new consultations, not in demo mode)
      if (!user) {
        console.warn('No user logged in - consultation will not be saved');
        setSaveError('Not logged in - the consultation will not be saved');
      } else if (!patientInfo || !patientInfo.prenom || !patientInfo.nom || !patientInfo.dateNaissance) {
        console.warn('Patient info missing or incomplete:', patientInfo);
        setSaveError('Patient information missing (last name, first name, date of birth) - the consultation will not be saved');
      }
    }

    // Generate PDF and download directly
    const liveReportData = getLiveReportData();

    // Create a temporary container for PDF generation
    const pdfContainer = document.createElement('div');
    pdfContainer.innerHTML = `
      <div style="width: 595px; font-family: 'Segoe UI', Roboto, Arial, sans-serif; background: white; margin: 0; padding: 0;">
        <!-- Header with gradient - full width -->
        <div style="background: linear-gradient(135deg, #059669 0%, #10b981 50%, #14b8a6 100%); padding: 20px 24px; width: 100%; box-sizing: border-box;">
          <table style="width: 100%; border-collapse: collapse;">
            <tr>
              <td style="vertical-align: middle;">
                <table style="border-collapse: collapse;">
                  <tr>
                    <td style="vertical-align: middle; padding-right: 12px;">
                      <div style="width: 36px; height: 36px; background: rgba(255,255,255,0.2); border-radius: 10px; text-align: center; line-height: 36px;">
                        <span style="color: white; font-size: 18px;">+</span>
                      </div>
                    </td>
                    <td style="vertical-align: middle;">
                      <div style="color: white; font-size: 16px; font-weight: 600;">MediScan - Rapport de consultation</div>
                      <div style="color: rgba(255,255,255,0.8); font-size: 10px; margin-top: 2px;">${escapeHtml(liveReportData.meta.clinic)} - ${escapeHtml(liveReportData.meta.date)}</div>
                    </td>
                  </tr>
                </table>
              </td>
              <td style="text-align: right; vertical-align: middle;">
                <div style="color: rgba(255,255,255,0.6); font-size: 9px; text-transform: uppercase; letter-spacing: 0.5px;">Record ref.</div>
                <div style="color: white; font-size: 11px; font-weight: 600; margin-top: 2px;">${escapeHtml(liveReportData.meta.ref)}</div>
              </td>
            </tr>
          </table>
        </div>

        <!-- Main Content -->
        <div style="padding: 16px 20px 8px 20px; background: white;">
          <!-- Patient & Doctor Cards -->
          <table style="width: 100%; border-collapse: separate; border-spacing: 10px 0; margin-bottom: 14px;">
            <tr>
              <!-- Patient Card -->
              <td style="width: 50%; background: linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%); border: 1px solid #d1fae5; border-radius: 12px; padding: 12px; vertical-align: top;">
                <table style="border-collapse: collapse;">
                  <tr>
                    <td style="vertical-align: top; padding-right: 8px;">
                      <div style="width: 28px; height: 28px; background: #059669; border-radius: 50%; text-align: center; line-height: 28px;">
                        <span style="color: white; font-size: 12px; font-weight: 600;">${escapeHtml(liveReportData.patient.name.charAt(0))}</span>
                      </div>
                    </td>
                    <td style="vertical-align: top;">
                      <div style="font-size: 13px; font-weight: 600; color: #0f172a;">${escapeHtml(liveReportData.patient.name)}</div>
                      <div style="font-size: 9px; color: #64748b;">ID: ${escapeHtml(liveReportData.patient.id)}</div>
                      <table style="border-collapse: separate; border-spacing: 6px 0; margin-top: 6px;">
                        <tr>
                          <td style="background: white; border: 1px solid #d1fae5; border-radius: 20px; padding: 2px 8px; font-size: 9px; color: #047857; font-weight: 500; white-space: nowrap;">${escapeHtml(liveReportData.patient.age)} ans</td>
                          <td style="background: white; border: 1px solid #d1fae5; border-radius: 20px; padding: 2px 8px; font-size: 9px; color: #047857; font-weight: 500; white-space: nowrap;">${escapeHtml(liveReportData.patient.sex)}</td>
                        </tr>
                      </table>
                    </td>
                  </tr>
                </table>
              </td>
              <!-- Doctor Card -->
              <td style="width: 50%; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; padding: 12px; vertical-align: top;">
                <div style="font-size: 9px; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px;">Attending physician</div>
                <div style="font-size: 12px; font-weight: 600; color: #0f172a;">${escapeHtml(liveReportData.meta.practitioner)}</div>
                <div style="font-size: 9px; color: #64748b; margin-top: 4px;">${escapeHtml(liveReportData.soap.consultation) || 'General consultation'}</div>
              </td>
            </tr>
          </table>

          <!-- Constantes Card -->
          ${liveReportData.soap.constantes && liveReportData.soap.constantes !== 'Vital signs not recorded' ? `
          <div style="background: linear-gradient(135deg, #eff6ff 0%, #f0f9ff 100%); border: 1px solid #bfdbfe; border-radius: 10px; padding: 10px 14px; margin-bottom: 12px;">
            <table style="border-collapse: collapse; margin-bottom: 6px;">
              <tr>
                <td style="width: 14px; height: 14px; background: #3b82f6; border-radius: 4px; vertical-align: middle;"></td>
                <td style="padding-left: 6px; font-size: 9px; font-weight: 600; color: #1e40af; text-transform: uppercase; letter-spacing: 0.5px; vertical-align: middle;">Vital Signs</td>
              </tr>
            </table>
            <div style="font-size: 10px; color: #1e293b; line-height: 1.4;">${escapeHtml(liveReportData.soap.constantes)}</div>
          </div>
          ` : ''}

          <!-- Medical Sections -->
          ${liveReportData.soap.motif ? `
          <div style="background: white; border: 1px solid #e2e8f0; border-radius: 10px; padding: 10px 14px; margin-bottom: 8px;">
            <table style="border-collapse: collapse; margin-bottom: 6px;">
              <tr>
                <td style="width: 14px; height: 14px; background: linear-gradient(135deg, #059669, #10b981); border-radius: 4px; vertical-align: middle;"></td>
                <td style="padding-left: 6px; font-size: 9px; font-weight: 600; color: #059669; text-transform: uppercase; letter-spacing: 0.5px; vertical-align: middle;">Chief Complaint</td>
              </tr>
            </table>
            <div style="font-size: 10px; color: #334155; line-height: 1.5;">${escapeHtml(liveReportData.soap.motif)}</div>
          </div>
          ` : ''}

          ${liveReportData.soap.histoire_maladie ? `
          <div style="background: white; border: 1px solid #e2e8f0; border-radius: 10px; padding: 10px 14px; margin-bottom: 8px;">
            <table style="border-collapse: collapse; margin-bottom: 6px;">
              <tr>
                <td style="width: 14px; height: 14px; background: linear-gradient(135deg, #059669, #10b981); border-radius: 4px; vertical-align: middle;"></td>
                <td style="padding-left: 6px; font-size: 9px; font-weight: 600; color: #059669; text-transform: uppercase; letter-spacing: 0.5px; vertical-align: middle;">History of Present Illness</td>
              </tr>
            </table>
            <div style="font-size: 10px; color: #334155; line-height: 1.5;">${escapeHtml(liveReportData.soap.histoire_maladie)}</div>
          </div>
          ` : ''}

          ${liveReportData.soap.antecedents ? `
          <div style="background: white; border: 1px solid #e2e8f0; border-radius: 10px; padding: 10px 14px; margin-bottom: 8px;">
            <table style="border-collapse: collapse; margin-bottom: 6px;">
              <tr>
                <td style="width: 14px; height: 14px; background: linear-gradient(135deg, #059669, #10b981); border-radius: 4px; vertical-align: middle;"></td>
                <td style="padding-left: 6px; font-size: 9px; font-weight: 600; color: #059669; text-transform: uppercase; letter-spacing: 0.5px; vertical-align: middle;">Past Medical History</td>
              </tr>
            </table>
            <div style="font-size: 10px; color: #334155; line-height: 1.5;">${escapeHtml(liveReportData.soap.antecedents)}</div>
          </div>
          ` : ''}

          ${liveReportData.soap.examen_clinique ? `
          <div style="background: white; border: 1px solid #e2e8f0; border-radius: 10px; padding: 10px 14px; margin-bottom: 8px;">
            <table style="border-collapse: collapse; margin-bottom: 6px;">
              <tr>
                <td style="width: 14px; height: 14px; background: linear-gradient(135deg, #059669, #10b981); border-radius: 4px; vertical-align: middle;"></td>
                <td style="padding-left: 6px; font-size: 9px; font-weight: 600; color: #059669; text-transform: uppercase; letter-spacing: 0.5px; vertical-align: middle;">Clinical Examination</td>
              </tr>
            </table>
            <div style="font-size: 10px; color: #334155; line-height: 1.5;">${escapeHtml(liveReportData.soap.examen_clinique)}</div>
          </div>
          ` : ''}

          ${liveReportData.soap.examens_complementaires ? `
          <div style="background: white; border: 1px solid #e2e8f0; border-radius: 10px; padding: 10px 14px; margin-bottom: 8px;">
            <table style="border-collapse: collapse; margin-bottom: 6px;">
              <tr>
                <td style="width: 14px; height: 14px; background: linear-gradient(135deg, #059669, #10b981); border-radius: 4px; vertical-align: middle;"></td>
                <td style="padding-left: 6px; font-size: 9px; font-weight: 600; color: #059669; text-transform: uppercase; letter-spacing: 0.5px; vertical-align: middle;">Additional Tests</td>
              </tr>
            </table>
            <div style="font-size: 10px; color: #334155; line-height: 1.5;">${escapeHtml(liveReportData.soap.examens_complementaires)}</div>
          </div>
          ` : ''}

          ${liveReportData.soap.conclusion_traitement ? `
          <div style="background: white; border: 1px solid #e2e8f0; border-radius: 10px; padding: 10px 14px; margin-bottom: 8px;">
            <table style="border-collapse: collapse; margin-bottom: 6px;">
              <tr>
                <td style="width: 14px; height: 14px; background: linear-gradient(135deg, #059669, #10b981); border-radius: 4px; vertical-align: middle;"></td>
                <td style="padding-left: 6px; font-size: 9px; font-weight: 600; color: #059669; text-transform: uppercase; letter-spacing: 0.5px; vertical-align: middle;">Conclusion and Treatment</td>
              </tr>
            </table>
            <div style="font-size: 10px; color: #334155; line-height: 1.5;">${escapeHtml(liveReportData.soap.conclusion_traitement)}</div>
          </div>
          ` : ''}

          ${liveReportData.soap.ordonnance ? `
          <div style="background: linear-gradient(135deg, #fefce8 0%, #fef9c3 100%); border: 1px solid #fde047; border-radius: 10px; padding: 10px 14px; margin-bottom: 8px;">
            <table style="border-collapse: collapse; margin-bottom: 6px;">
              <tr>
                <td style="width: 14px; height: 14px; background: #eab308; border-radius: 4px; vertical-align: middle;"></td>
                <td style="padding-left: 6px; font-size: 9px; font-weight: 600; color: #a16207; text-transform: uppercase; letter-spacing: 0.5px; vertical-align: middle;">Prescription</td>
              </tr>
            </table>
            <div style="font-size: 10px; color: #422006; line-height: 1.5;">${escapeHtml(liveReportData.soap.ordonnance)}</div>
          </div>
          ` : ''}

          <!-- Signature section -->
          <table style="width: 100%; border-collapse: collapse; margin-top: 16px;">
            <tr>
              <td style="text-align: right;">
                <div style="display: inline-block; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 10px; padding: 12px 16px; text-align: center;">
                  <div style="font-size: 9px; color: #64748b; margin-bottom: 8px;">Done at ${escapeHtml(liveReportData.meta.clinic)}, on ${escapeHtml(liveReportData.meta.date)}</div>
                  <div style="border: 2px dashed #cbd5e1; border-radius: 8px; width: 140px; height: 50px; line-height: 50px; text-align: center; background: white;">
                    <span style="font-size: 9px; color: #94a3b8;">Stamp and signature</span>
                  </div>
                </div>
              </td>
            </tr>
          </table>
        </div>

        <!-- Footer - full width outside content padding -->
        <div style="background: #f8fafc; padding: 10px 24px; border-top: 1px solid #e2e8f0; width: 100%; box-sizing: border-box;">
          <table style="width: 100%; border-collapse: collapse;">
            <tr>
              <td style="font-size: 8px; color: #64748b; vertical-align: middle;">Generated on ${escapeHtml(liveReportData.meta.date)} - ${escapeHtml(liveReportData.meta.practitioner)}</td>
              <td style="text-align: right; font-size: 8px; color: #64748b; vertical-align: middle;">
                <span style="display: inline-block; width: 6px; height: 6px; background: #10b981; border-radius: 50%; margin-right: 4px; vertical-align: middle;"></span><span style="display: inline-block; vertical-align: middle;">AI-assisted - human verification required</span>
              </td>
            </tr>
          </table>
        </div>
      </div>
    `;

    // Configure PDF options - simple settings, let content flow naturally
    const opt = {
      margin: [8, 8, 8, 8],
      filename: `Consultation_${liveReportData.patient.name.replace(/\s+/g, '_')}_${liveReportData.meta.date.replace(/\//g, '-')}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true, logging: false },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
    };

    // Generate and download PDF
    html2pdf().set(opt).from(pdfContainer).save();
  };

  const updateSection = (sectionName, value) => {
    setSections(prev => ({
      ...prev,
      [sectionName]: value
    }));

    // Remove from highlighted sections if now filled (>= 10 chars)
    if (value.trim().length >= 10 && highlightedSections.includes(sectionName)) {
      setHighlightedSections(prev => prev.filter(s => s !== sectionName));
    }
  };

  // Scroll to first empty section
  const scrollToFirstEmptySection = () => {
    if (highlightedSections.length > 0) {
      const element = document.getElementById(`section-${highlightedSections[0]}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  };

  // Auto-dismiss warning when all sections are filled
  useEffect(() => {
    if (highlightedSections.length === 0 && validationWarning) {
      setValidationWarning(null);
    }
  }, [highlightedSections, validationWarning]);

  // Note: constantes removed from here - it's handled separately with structured fields
  // Note: patient section removed - patient info is captured in structured fields at the top (nom, prenom, etc.)
  const sections_config = [
    { key: 'consultation', title: 'Consultation', placeholder: 'General consultation information...', emoji: '🏥', colorClass: 'from-emerald-500 to-green-600' },
    // medecin section removed from editable sections - it's auto-populated from doctorInfo and not user-editable
    { key: 'motif_consultation', title: 'Chief Complaint', placeholder: 'Describe the main reason for the visit...', emoji: '❓', colorClass: 'from-emerald-500 to-green-500' },
    { key: 'histoire_maladie', title: 'History of Present Illness', placeholder: 'History and progression of current symptoms...', emoji: '📋', colorClass: 'from-green-500 to-emerald-600' },
    { key: 'antecedents', title: 'Past Medical History', placeholder: 'Medical, surgical, family history...', emoji: '🧬', colorClass: 'from-teal-500 to-emerald-600' },
    { key: 'examen_clinique', title: 'Clinical Examination', placeholder: 'Physical examination performed by the physician...', emoji: '🩺', colorClass: 'from-emerald-600 to-green-600' },
    { key: 'examens_complementaires', title: 'Additional Tests', placeholder: 'Lab work, radiology, biological tests...', emoji: '🔬', colorClass: 'from-green-600 to-teal-600' },
    { key: 'ordonnance', title: 'Prescription', placeholder: 'Prescriptions, treatments...', emoji: '💊', colorClass: 'from-emerald-500 to-green-600' },
    { key: 'conclusion_traitement', title: 'Conclusion and Treatment Plan', placeholder: 'Diagnosis and therapeutic strategy...', emoji: '⚕️', colorClass: 'from-green-600 to-emerald-700' }
  ];

  const constantes_config = [
    { key: 'temperature', title: 'Temperature', placeholder: 'E.g. 37.2°C', unit: '°C' },
    { key: 'tension', title: 'Blood pressure', placeholder: 'E.g. 120/80', unit: 'mmHg' },
    { key: 'frequenceCardiaque', title: 'Heart rate', placeholder: 'E.g. 72', unit: 'bpm' },
    { key: 'glycemie', title: 'Blood glucose', placeholder: 'E.g. 1.2', unit: 'g/L' },
    { key: 'saturation', title: 'O2 saturation', placeholder: 'E.g. 98', unit: '%' },
    { key: 'poids', title: 'Weight', placeholder: 'E.g. 70', unit: 'kg' },
    { key: 'taille', title: 'Height', placeholder: 'E.g. 175', unit: 'cm' }
  ];

  const generateConstantesText = () => {
    const entries = [];
    // Use local constantes state (which is synced with patientConstantes prop)
    if (constantes.temperature) entries.push(`Temperature: ${constantes.temperature}°C`);
    if (constantes.tension) entries.push(`BP: ${constantes.tension} mmHg`);
    if (constantes.frequenceCardiaque) entries.push(`HR: ${constantes.frequenceCardiaque} bpm`);
    if (constantes.saturation) entries.push(`O2 Sat: ${constantes.saturation}%`);
    if (constantes.glycemie) entries.push(`Blood glucose: ${constantes.glycemie} g/L`);
    if (constantes.poids) entries.push(`Weight: ${constantes.poids} kg`);
    if (constantes.taille) entries.push(`Height: ${constantes.taille} cm`);

    return entries.length > 0 ? entries.join('\n') : 'Vital signs not recorded';
  };

  // Translation functions
  const translateReport = async (targetLang) => {
    if (targetLang === originalLanguage) {
      setCurrentLanguage(targetLang);
      setTargetLanguage('');
      return;
    }

    // Check if translation already exists
    if (translations[targetLang]) {
      setSections(translations[targetLang]);
      setCurrentLanguage(targetLang);
      setTargetLanguage('');
      return;
    }

    setIsTranslating(true);
    setTargetLanguage(targetLang);
    try {
      console.log('Starting translation to:', targetLang);
      console.log('Sections to translate:', sections);
      
      const response = await authFetch(getApiUrl(`/api/edit-report/translate?lang=${targetLang}&reportID=${reportId}`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sections,
          sourceLanguage: originalLanguage,
          targetLanguage: targetLang
        }),
      });

      console.log('Translation response status:', response.status);
      
      if (response.ok) {
        const translatedSections = await response.json();
        console.log('Translated sections:', translatedSections);
        
        // Cache the translation
        setTranslations(prev => ({
          ...prev,
          [targetLang]: translatedSections
        }));
        
        setSections(translatedSections);
        setCurrentLanguage(targetLang);
      } else {
        const errorText = await response.text();
        console.error('Translation failed:', response.status, errorText);
        throw new Error(`Translation failed: ${response.status} - ${errorText}`);
      }
    } catch (error) {
      console.error('Translation error:', error);
      alert(`❌ Translation error: ${error.message}`);
      // Reset dropdown to original language on error
      setCurrentLanguage(originalLanguage);
    } finally {
      setIsTranslating(false);
      setTargetLanguage('');
    }
  };

  const returnToOriginal = () => {
    if (translations[originalLanguage]) {
      setSections(translations[originalLanguage]);
    }
    setCurrentLanguage(originalLanguage);
  };

  // Generate live report data for the preview
  const getLiveReportData = () => {
    const currentDate = new Date().toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
    const currentTime = new Date().toLocaleTimeString('en-US');

    return {
      meta: {
        clinic: doctorInfo.place || 'General Medical Center',
        practitioner: `Dr. ${doctorInfo.name || 'A. Smith'} (${doctorInfo.specialty || 'NPI 123456789'})`,
        date: currentDate,
        ref: `CONS-${new Date().toISOString().split('T')[0]}-${currentTime.replace(/:/g, 'h').replace(/h\d+$/, 'h')}`
      },
      patient: {
        id: `P-${new Date().getFullYear()}-${String(Math.floor(Math.random() * 9999)).padStart(4, '0')}`,
        name: patientInfo && (patientInfo.nom || patientInfo.prenom) ?
              `${patientInfo.prenom || ''} ${patientInfo.nom || ''}`.trim() :
              "Patient (anonymized)",
        age: patientInfo && patientInfo.dateNaissance ?
             new Date().getFullYear() - new Date(patientInfo.dateNaissance).getFullYear() :
             "Not specified",
        sex: patientInfo && patientInfo.sexe ?
             (patientInfo.sexe === 'M' ? 'Male' : 'Female') :
             "Not specified",
        weight: constantes && constantes.poids ?
                `${constantes.poids} kg` :
                "Not specified",
        height: constantes && constantes.taille ?
                `${constantes.taille} cm` :
                "Not specified",
        allergies: ["None known"],
        comorbidities: ["Family history of hypertension"]
      },
      soap: {
        motif: sections.motif_consultation || 'Chronic low back pain with right sciatic radiation, worsening over the past week.',
        histoire_maladie: sections.histoire_maladie || 'Progressive onset 6 months ago. Mechanical-type pain, aggravated by effort and prolonged sitting. Relative improvement with rest. No motor deficit. History of common low back pain 2 years ago.',
        antecedents: sections.antecedents || 'Family history of hypertension. No surgical history.',
        examen_clinique: sections.examen_clinique || 'Patient in good general condition. Neurological exam: deep tendon reflexes preserved and symmetrical, no sensorimotor deficit, positive straight leg raise at 60° on the right. Palpation: paravertebral contracture L4-L5-S1.',
        examens_complementaires: sections.examens_complementaires || 'Lumbar spine X-ray: moderate L5-S1 disc disease.',
        conclusion_traitement: sections.conclusion_traitement || 'Right common lumbosciatica L5-S1, probably of disco-radicular origin. No cauda equina syndrome.',
        ordonnance: sections.ordonnance || 'Relative rest 48-72h. Analgesics: Paracetamol 1g x3/day. Anti-inflammatories: Ibuprofen 400mg x3/day for 5 days (if no contraindication). Physical therapy after acute phase.',
        suivi: 'Reassessment in 8-10 days. If no improvement or worsening: consider lumbar MRI and specialist referral (rheumatology/neurosurgery).',
        observations: 'Patient informed of warning signs (motor deficit, sphincter disorders). Hygiene-dietary advice given (ergonomics, exercises).',
        constantes: generateConstantesText()
      }
    };
  };

  // Show loading animation while extracting facts
  if (isExtractingFacts) {
    return (
      <div className="edit-report-container" style={{ 
        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 25%, #f1f5f9 50%, #e0e7ff 75%, #fdf2f8 100%)',
        backgroundSize: '400% 400%',
        animation: 'gradientShift 15s ease infinite',
        position: 'relative'
      }}>
        <LoadingFacts />
      </div>
    );
  }

  return (
    <div className="edit-report-container" style={{ 
      background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 25%, #f1f5f9 50%, #e0e7ff 75%, #fdf2f8 100%)',
      backgroundSize: '400% 400%',
      animation: 'gradientShift 15s ease infinite',
      position: 'relative'
    }}>

      {/* Header */}
      <div className="edit-report-header">
        <div className="header-info" style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
          {onNavigateHome && (
            <button
              onClick={onNavigateHome}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                transition: 'opacity 0.2s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
              onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
              title="Back to home"
            >
              <svg style={{ width: '32px', height: '32px', color: '#059669' }} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2L21.5 7v10L12 22 2.5 17V7L12 2z" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                <path d="M9 9h6M12 6v12M9 15h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              <span style={{ fontSize: '20px', fontWeight: '600', color: '#111827' }}>MediScan</span>
            </button>
          )}
          <div>
            <h1>📋 Consultation Report Editor</h1>
            <div className="doctor-info">
              <span>Dr. {doctorInfo.name || 'Not specified'}</span>
              <span className="separator">•</span>
              <span>{doctorInfo.specialty || 'Not specified'}</span>
              <span className="separator">•</span>
              <span>{new Date().toLocaleDateString('en-US')}</span>
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-4">
          <button
            className="inline-flex items-center px-4 py-2.5 text-sm font-semibold text-gray-700 bg-white/90 backdrop-blur-sm border border-gray-200 rounded-xl shadow-sm hover:bg-white hover:shadow-md focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed group"
            onClick={onCancel}
            disabled={isLoading}
          >
            <svg className="w-4 h-4 mr-2 group-hover:-translate-x-0.5 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back
          </button>

          {/* Translation Dropdown - Enhanced */}
          <div className="relative">
            <select
              value={currentLanguage}
              onChange={(e) => translateReport(e.target.value)}
              disabled={isTranslating}
              className="inline-flex items-center px-4 py-2.5 text-sm font-semibold text-gray-700 bg-white/90 backdrop-blur-sm border border-gray-200 rounded-xl shadow-sm hover:bg-white hover:shadow-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed appearance-none cursor-pointer pr-10"
              style={{ minWidth: '150px' }}
            >
              {LANGUAGES.map(lang => (
                <option key={lang.code} value={lang.code}>
                  {lang.flag} {lang.name}
                </option>
              ))}
            </select>

            {/* Custom dropdown arrow or loading spinner */}
            <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
              {isTranslating ? (
                <div className="w-4 h-4 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              )}
            </div>
          </div>

          {/* Return to original button when translated */}
          {currentLanguage !== originalLanguage && (
            <button
              className="inline-flex items-center px-3 py-2 text-xs font-semibold text-blue-700 bg-blue-50/90 backdrop-blur-sm border border-blue-200 rounded-lg shadow-sm hover:bg-blue-100 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={returnToOriginal}
              disabled={isTranslating}
              title="Return to original language (French)"
            >
              <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
              </svg>
              FR Origine
            </button>
          )}

          <button
            className="inline-flex items-center px-6 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-emerald-600 to-green-600 rounded-xl shadow-lg hover:from-emerald-700 hover:to-green-700 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105"
            onClick={handleExport}
            disabled={isLoading || isSaving}
          >
            {isSaving ? (
              <>
                <div className="w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                {env.IS_DEMO ? 'Downloading...' : 'Saving...'}
              </>
            ) : (
              <>
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                {env.IS_DEMO
                  ? (isLoading ? 'Downloading...' : 'Download')
                  : (isLoading ? 'Saving...' : 'Save')}
              </>
            )}
          </button>
        </div>
      </div>

      {/* Error Message for Save Failure */}
      {saveError && (
        <div className="mx-6 mt-4 bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded-lg flex items-start gap-3">
          <svg className="w-5 h-5 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          <div className="flex-1">
            <p className="font-semibold text-sm">Save warning</p>
            <p className="text-xs mt-1">The consultation could not be saved: {saveError}</p>
            <p className="text-xs mt-1">The PDF report will be generated, but the data is not stored in the database.</p>
          </div>
          <button onClick={() => setSaveError(null)} className="text-yellow-600 hover:text-yellow-800">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
      )}

      {/* Translation Loading Overlay - Fixed position to cover entire viewport */}
      {isTranslating && (
        <div className="fixed inset-0 bg-white/70 backdrop-blur-sm z-[100] flex items-center justify-center">
          <div className="bg-white rounded-2xl shadow-2xl p-8 flex flex-col items-center space-y-4 border border-gray-100">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
              <span className="text-2xl">🌐</span>
            </div>
            <div className="text-center">
              <h3 className="text-lg font-semibold text-gray-900">Translation in progress</h3>
              <p className="text-sm text-gray-600 mt-1">
                {targetLanguage ? `To ${LANGUAGES.find(l => l.code === targetLanguage)?.name || targetLanguage}...` : 'Translating...'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Split View: Form + Live Preview */}
      <div className="edit-report-split-view">
        {/* Left Side - Modern Form with Tailwind */}
        <div className="form-panel isolate relative overflow-y-auto bg-white/60 backdrop-blur-xl border-r border-gray-200/30">
          {/* Form content with enhanced Tailwind styling */}
          <div className="relative px-8 py-10 min-h-full max-w-2xl mx-auto">
            {/* Enhanced decorative elements */}
            <div className="absolute top-20 left-8 w-64 h-64 bg-gradient-to-br from-purple-200 to-purple-300 rounded-full mix-blend-multiply filter blur-2xl opacity-20 animate-pulse"></div>
            <div className="absolute bottom-20 right-8 w-56 h-56 bg-gradient-to-br from-blue-200 to-cyan-300 rounded-full mix-blend-multiply filter blur-2xl opacity-20 animate-pulse delay-1000"></div>
            <div className="absolute top-1/2 left-1/4 w-40 h-40 bg-gradient-to-br from-indigo-200 to-blue-300 rounded-full mix-blend-multiply filter blur-xl opacity-15 animate-pulse delay-2000"></div>

            {/* Enhanced Header */}
            <div className="relative z-10 mx-auto max-w-xl text-center mb-12">
              <h2 className="text-3xl font-bold tracking-tight bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent mb-4" style={{ fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}>
                Consultation report
                {isTranslating && (
                  <span className="ml-3 inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-primary-50 to-primary-100 text-primary-700 animate-pulse border border-primary-200">
                    <div className="w-2 h-2 border border-primary-500 border-t-transparent rounded-full animate-spin mr-2"></div>
                    🌐 Translating...
                  </span>
                )}
                {currentLanguage !== originalLanguage && !isTranslating && (
                  <span className="ml-3 inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-blue-50 to-blue-100 text-blue-700 border border-blue-200 shadow-sm">
                    🌐 {LANGUAGES.find(l => l.code === currentLanguage)?.name}
                  </span>
                )}
              </h2>
              <p className="text-base leading-7 text-gray-600 font-medium">
                {currentLanguage !== originalLanguage
                  ? "Medical information entry"
                  : "Medical information entry"
                }
              </p>
            </div>

            {/* Enhanced Form with Floating Labels */}
            <form className="relative z-10 mx-auto max-w-4xl space-y-10">
              <div className="space-y-10">

                {/* Enhanced Vital Signs Section with Floating Labels */}
                <div className="group relative">
                  <div className="absolute -inset-1 bg-gradient-to-r from-emerald-600 to-green-600 rounded-2xl blur opacity-20 group-hover:opacity-30 transition duration-1000 group-hover:duration-200"></div>
                  <div className="relative bg-white/90 backdrop-blur-sm rounded-2xl p-8 border border-white/20 shadow-xl">
                    <div className="flex items-center mb-8">
                      <div className="flex items-center justify-center w-10 h-10 bg-gradient-to-br from-emerald-500 to-green-500 rounded-xl mr-4 shadow-lg">
                        <span className="text-lg text-white">🩺</span>
                      </div>
                      <h3 className="text-xl font-bold text-gray-900" style={{ fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}>Vital Signs</h3>
                    </div>

                    {/* Grouped Constantes - Simple Inline Layout */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {constantes_config.map((constante) => (
                        <div key={constante.key} className="flex flex-col">
                          <label className="text-xs text-gray-500 mb-1" htmlFor={`floating_${constante.key}`}>
                            {constante.title}
                          </label>
                          <div className="flex items-baseline gap-1">
                            <input
                              type="text"
                              name={`floating_${constante.key}`}
                              id={`floating_${constante.key}`}
                              value={constantes[constante.key]}
                              onChange={(e) => setConstantes(prev => ({ ...prev, [constante.key]: e.target.value }))}
                              className="text-base font-medium text-gray-900 border-b border-gray-300 focus:border-blue-500 focus:outline-none bg-transparent pb-1 transition-colors"
                              style={{ width: constante.key === 'tension' ? '90px' : constante.key === 'temperature' ? '45px' : '60px' }}
                              placeholder="--"
                              disabled={isLoading}
                            />
                            <span className="text-xs text-gray-400 ml-1 whitespace-nowrap">{constante.unit}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Enhanced Medical Sections with Floating Labels */}
                {sections_config.map((section) => (
                  <div
                    key={section.key}
                    id={`section-${section.key}`}
                    className="group relative"
                  >
                    <div className={`absolute -inset-1 bg-gradient-to-r ${highlightedSections.includes(section.key) ? 'from-amber-500 to-orange-500' : 'from-emerald-600 to-green-600'} rounded-2xl blur ${highlightedSections.includes(section.key) ? 'opacity-30' : 'opacity-15'} group-hover:opacity-25 transition duration-1000 group-hover:duration-200`}></div>
                    <div className={`relative bg-white/90 backdrop-blur-sm rounded-2xl p-8 shadow-xl ${highlightedSections.includes(section.key) ? 'border-2 border-amber-400' : 'border border-white/20'}`}>
                      {/* Empty section warning badge */}
                      {highlightedSections.includes(section.key) && (
                        <div style={{
                          background: '#fef3c7',
                          padding: '8px 12px',
                          borderRadius: '8px',
                          marginBottom: '12px',
                          fontSize: '12px',
                          color: '#92400e',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}>
                          <span>⚠️</span>
                          <span>This section appears empty - please fill it in manually</span>
                        </div>
                      )}
                      <div className="flex items-center mb-8">
                        <div className={`flex items-center justify-center w-10 h-10 bg-gradient-to-br ${highlightedSections.includes(section.key) ? 'from-amber-500 to-orange-500' : section.colorClass} rounded-xl mr-4 shadow-lg`}>
                          <span className="text-lg text-white">{section.emoji}</span>
                        </div>
                        <h3 className="text-xl font-bold text-gray-900" style={{ fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}>{section.title}</h3>
                      </div>

                      <div className="relative z-0 w-full group">
                        <textarea
                          name={`floating_${section.key}`}
                          id={`floating_${section.key}`}
                          value={sections[section.key]}
                          onChange={(e) => updateSection(section.key, e.target.value)}
                          rows={section.key === 'observations' ? 6 : 4}
                          className={`block py-3 px-0 w-full text-sm text-gray-900 bg-transparent border-0 border-b-2 ${highlightedSections.includes(section.key) ? 'border-amber-400 focus:border-amber-500' : 'border-gray-300 focus:border-primary-600'} appearance-none focus:outline-none focus:ring-0 peer resize-none transition-colors duration-300`}
                          placeholder=" "
                          disabled={isLoading}
                        />
                        <label
                          htmlFor={`floating_${section.key}`}
                          className={`peer-focus:font-medium absolute text-sm ${highlightedSections.includes(section.key) ? 'text-amber-600' : 'text-gray-500'} duration-300 transform -translate-y-6 scale-75 top-3 -z-10 origin-[0] peer-focus:start-0 ${highlightedSections.includes(section.key) ? 'peer-focus:text-amber-600' : 'peer-focus:text-primary-600'} peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-6`}
                        >
                          {section.placeholder}
                        </label>

                        {/* Character count indicator */}
                        <div className={`mt-3 text-xs ${highlightedSections.includes(section.key) ? 'text-amber-600' : 'text-gray-500'} text-right`}>
                          {sections[section.key]?.length || 0} characters
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

              </div>
            </form>
          </div>
        </div>

        {/* Right Side - Live Preview */}
        <div className="preview-panel flex flex-col overflow-auto max-h-screen bg-slate-50/30">
          <div className="sticky top-0 bg-white/95 backdrop-blur-sm z-10 border-b border-gray-200/50 shadow-sm">
            <div className="flex items-center justify-center py-6">
              <div className="flex items-center gap-3">
                <h2 className="text-xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                  Report preview
                </h2>
              </div>
            </div>
          </div>
          <div className="flex-1 p-8">
            <div className="w-full flex justify-center">
              <ReportPreview reportData={getLiveReportData()} />
            </div>
          </div>
        </div>
      </div>

      {/* Extraction Quality Warning Modal */}
      {validationWarning && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '24px 28px',
            maxWidth: '480px',
            width: '90%',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            animation: 'modalFadeIn 0.2s ease-out'
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <span style={{ fontSize: '24px' }}>⚠️</span>
              </div>
              <div style={{ flex: 1 }}>
                <h3 style={{ margin: '0 0 8px 0', color: '#92400e', fontSize: '18px', fontWeight: '600' }}>
                  Potentially incomplete extraction
                </h3>
                <p style={{ margin: '0 0 16px 0', color: '#78350f', fontSize: '14px', lineHeight: '1.6' }}>
                  {validationWarning.emptyCount} critical section(s) appear empty or incomplete.
                  This may indicate a problem with the transcription or AI extraction.
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '20px' }}>
                  {validationWarning.emptySections.map(sectionKey => (
                    <span key={sectionKey} style={{
                      background: '#fef3c7',
                      color: '#92400e',
                      padding: '6px 14px',
                      borderRadius: '20px',
                      fontSize: '13px',
                      fontWeight: '500',
                      border: '1px solid #fcd34d'
                    }}>
                      {getSectionLabel(sectionKey)}
                    </span>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={() => {
                      setValidationWarning(null);
                      scrollToFirstEmptySection();
                    }}
                    style={{
                      background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                      border: 'none',
                      color: 'white',
                      padding: '10px 20px',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      boxShadow: '0 4px 6px -1px rgba(245, 158, 11, 0.3)'
                    }}
                  >
                    Complete the sections
                  </button>
                  <button
                    type="button"
                    onClick={() => setValidationWarning(null)}
                    style={{
                      background: 'white',
                      border: '1px solid #e5e7eb',
                      color: '#6b7280',
                      padding: '10px 20px',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: 'pointer'
                    }}
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes modalFadeIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
      `}</style>
    </div>
  );
};

export default EditReport;